import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Minus, Plus, Trash2, MapPin, User, Mail, Phone, Loader2, Crown, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { useRazorpay } from 'react-razorpay';

const API = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const Checkout = () => {
  const { items, updateQuantity, removeItem, totalItems, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', delivery_address: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);

  // Pre-fill form from user data
  useEffect(() => {
    if (user && user.name) {
      setForm(f => ({
        ...f,
        customer_name: f.customer_name || user.name,
        customer_email: f.customer_email || user.email,
        customer_phone: f.customer_phone || user.phone
      }));
    }
  }, [user]);

  // Fetch discount info for logged-in users
  useEffect(() => {
    if (user) {
      fetch(`${API}/api/user/discount`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setDiscountInfo(d); })
        .catch(() => {});
    }
  }, [user]);

  const discountAmount = discountApplied && discountInfo?.can_apply
    ? Math.round(totalPrice * discountInfo.discount_percent / 100)
    : 0;
  const finalPrice = totalPrice - discountAmount;

  const handlePayment = useCallback(async (e) => {
    e.preventDefault();
    if (items.length === 0) return;

    setLoading(true);
    setError('');

    try {
      // Step 1: Create order on backend
      const res = await fetch(`${API}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity,
            image: i.image
          })),
          ...form
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Order creation failed');
      }

      const orderData = await res.json();

      // Step 2: Open Razorpay checkout modal
      const options = {
        key: RAZORPAY_KEY,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Nistala's Group",
        description: `Order #${orderData.order_id.slice(0, 8)}`,
        order_id: orderData.razorpay_order_id,
        handler: async (response) => {
          // Step 3: Verify payment on backend
          try {
            const verifyRes = await fetch(`${API}/api/checkout/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: orderData.order_id
              })
            });

            if (verifyRes.ok) {
              clearCart();
              navigate(`/order-success?order_id=${orderData.order_id}&status=success`);
            } else {
              navigate(`/order-success?order_id=${orderData.order_id}&status=failed`);
            }
          } catch {
            navigate(`/order-success?order_id=${orderData.order_id}&status=error`);
          }
        },
        prefill: {
          name: form.customer_name,
          email: form.customer_email,
          contact: form.customer_phone
        },
        notes: {
          order_id: orderData.order_id,
          delivery_address: form.delivery_address
        },
        theme: {
          color: '#5C7A3A'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setError(`Payment failed: ${response.error.description}`);
        setLoading(false);
      });
      rzp.open();

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }, [items, form, Razorpay, clearCart, navigate]);

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] pt-28 pb-12" data-testid="checkout-empty">
        <SEO title="Checkout" />
        <div className="max-w-lg mx-auto px-4 text-center">
          <ShoppingBag size={56} className="mx-auto text-[#D4A574]/30 mb-4" />
          <h1 className="text-2xl font-bold text-[#2C1810] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Your cart is empty</h1>
          <p className="text-sm text-[#7a6555] mb-6">Add some products before checking out.</p>
          <Link to="/products">
            <Button className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer" data-testid="go-to-products-btn">Browse Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0] pt-28 pb-12" data-testid="checkout-page">
      <SEO title="Checkout" />
      <div className="max-w-5xl mx-auto px-4">
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-[#2C1810] mb-8"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Checkout
        </motion.h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: Customer Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-2xl border border-[#E8DFD3] p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#2C1810] mb-5" style={{ fontFamily: 'Playfair Display, serif' }}>
                Delivery Details
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-4" data-testid="checkout-error">
                  {error}
                </div>
              )}

              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                    <User size={14} className="text-[#7a6555]" /> Full Name
                  </label>
                  <Input
                    data-testid="checkout-name"
                    value={form.customer_name}
                    onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <Mail size={14} className="text-[#7a6555]" /> Email
                    </label>
                    <Input
                      data-testid="checkout-email"
                      type="email"
                      value={form.customer_email}
                      onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <Phone size={14} className="text-[#7a6555]" /> Phone
                    </label>
                    <Input
                      data-testid="checkout-phone"
                      type="tel"
                      value={form.customer_phone}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setForm(f => ({ ...f, customer_phone: val }));
                      }}
                      placeholder="10-digit mobile number"
                      pattern="\d{10}"
                      maxLength={10}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#7a6555]" /> Delivery Address
                  </label>
                  <Textarea
                    data-testid="checkout-address"
                    value={form.delivery_address}
                    onChange={e => setForm(f => ({ ...f, delivery_address: e.target.value }))}
                    placeholder="Full delivery address including area, city, pincode"
                    rows={3}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-5 mt-2 cursor-pointer"
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    <><Loader2 size={18} className="animate-spin mr-2" /> Processing...</>
                  ) : (
                    <>Pay {'\u20B9'}{finalPrice.toFixed(0)} Now</>
                  )}
                </Button>
                <p className="text-xs text-center text-[#7a6555] mt-2">
                  Secure payment powered by Razorpay. Supports UPI, Cards, Net Banking & Wallets.
                </p>
              </form>
            </div>
          </motion.div>

          {/* Right: Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl border border-[#E8DFD3] p-6 shadow-sm sticky top-28">
              <h2 className="text-lg font-bold text-[#2C1810] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Order Summary
              </h2>

              <div className="space-y-3 mb-4 max-h-[45vh] overflow-y-auto">
                {items.map(item => (
                  <div key={item.product_id} className="flex gap-3 p-2.5 bg-[#FDF8F0] rounded-xl" data-testid={`checkout-item-${item.product_id}`}>
                    <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2C1810] truncate">{item.name}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="w-6 h-6 rounded bg-white border border-[#E8DFD3] flex items-center justify-center cursor-pointer hover:bg-gray-50">
                          <Minus size={10} />
                        </button>
                        <span className="text-xs font-bold w-5 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="w-6 h-6 rounded bg-white border border-[#E8DFD3] flex items-center justify-center cursor-pointer hover:bg-gray-50">
                          <Plus size={10} />
                        </button>
                        <button onClick={() => removeItem(item.product_id)} className="ml-auto p-1 text-red-400 hover:text-red-600 cursor-pointer">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-[#2C1810] flex-shrink-0">{'\u20B9'}{(item.price * item.quantity).toFixed(0)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#E8DFD3] pt-3 space-y-2">
                <div className="flex justify-between text-sm text-[#7a6555]">
                  <span>Subtotal ({totalItems} items)</span>
                  <span>{'\u20B9'}{totalPrice.toFixed(0)}</span>
                </div>
                <div className="flex justify-between text-sm text-[#7a6555]">
                  <span>Delivery</span>
                  <span className="text-[#5C7A3A] font-medium">Free</span>
                </div>

                {/* Membership Discount Toggle */}
                {user && discountInfo?.can_apply && (
                  <div className="bg-[#5C7A3A]/5 rounded-xl p-3 border border-[#5C7A3A]/15" data-testid="membership-discount-section">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown size={14} className="text-[#5C7A3A]" />
                        <span className="text-xs font-medium text-[#2C1810]">{discountInfo.description}</span>
                      </div>
                      <button
                        onClick={() => setDiscountApplied(!discountApplied)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all ${
                          discountApplied ? 'bg-[#5C7A3A] text-white' : 'bg-white text-[#5C7A3A] border border-[#5C7A3A]'
                        }`}
                        data-testid="apply-discount-btn"
                      >
                        {discountApplied ? 'Applied' : 'Apply'}
                      </button>
                    </div>
                    {discountApplied && (
                      <p className="text-xs text-[#5C7A3A] mt-1.5">You save {'\u20B9'}{discountAmount} on this order!</p>
                    )}
                  </div>
                )}

                {!user && (
                  <div className="bg-[#FDF8F0] rounded-xl p-3 border border-[#E8DFD3]">
                    <div className="flex items-center gap-2">
                      <Gift size={14} className="text-[#C4844A]" />
                      <span className="text-xs text-[#7a6555]">
                        <Link to="/login" className="text-[#5C7A3A] font-semibold hover:underline">Login</Link> to apply membership discount & earn points
                      </span>
                    </div>
                  </div>
                )}

                {discountApplied && discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-[#5C7A3A] font-medium">
                    <span>Membership Discount</span>
                    <span>-{'\u20B9'}{discountAmount}</span>
                  </div>
                )}

                <div className="flex justify-between text-lg font-bold text-[#2C1810] pt-2 border-t border-[#E8DFD3]">
                  <span>Total</span>
                  <span>{'\u20B9'}{finalPrice.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
